import React, { useState, useEffect } from 'react';
import crossPink from '../../../images/crossPink.png';
import { format } from "date-fns";
import styles from './ContractSummary.module.scss';
import Warning from '../../../images/redAlert.png';
import CheckIcon from '@mui/icons-material/CheckCircle';
import ContractHeader from '../../../Components/ContractHeader';
import fileIcon from '../../../images/redAlert.png';
import yellowWarning from '../../../images/yellowWarning.png';
import CompletedIcon from '../../../images/checkImage.png';
import { Dialog, Classes, Icon, Intent, } from '@blueprintjs/core';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { Padding } from '@mui/icons-material';
import { TenantID, GET, PUT } from '../../dataSaver';
import { currentUser } from '../../../utils/auth';
import { ErrorToaster, SuccessToaster } from "../../../utils/toaster";
import { getServicesMissingFromTimesheets } from '../../ContractManager/checkDependentData';
import { config } from '../../../config';
import Tooltip from '@mui/material/Tooltip';

const MAX_CHAR_LIMIT = 500;

const ContractSummary = ({ isOpen = true, onClose, contractId, showActivationSection = false, onNavigateToTab, isAmendment = false, amendmentId = null, toDashboard }) => {
    const [contractData, setContractData] = useState(null);
    const [workflowData, setWorkflowData] = useState(null);
    const [amendmentData, setAmendmentData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [userNotes, setUserNotes] = useState('');
    const currentUserData = currentUser();
    const [charCount, setCharCount] = useState(0);
    const [siteInvoiceControlLevel, setSiteInvoiceControlLevel] = useState(null);

    // Map section titles to their corresponding tab names for navigation
    const sectionToTabMapping = {
        "Attached Contract Document(s)": "Attach Contract Documents",
        "Contract Identification & Term Limit": "Contract Identification & Term Limit",
        "Contracted Services Provider(s)": "Contracted Services Provider(s)",
        "Contractor Business Entity": "Contractor Business Entity",
        "Contracted Services Specification": "Contracted Services Specification",
        "Timesheet Submission Terms": "Timesheet Submission Terms",
        "Payment & Compensation": "Payment & Compensation",
    };

    // Sections that belong to the workflow configuration — always navigate to workflow dashboard
    const workflowSectionTitles = new Set([
        "Activity Logging",
        "Timesheet Creation & Submission",
        "Timesheet Review & Approval",
        "Invoice Review & Approval"
    ]);

    // Navigate to the workflow manager dashboard pre-searched to this contract
    const navigateToWorkflowDashboard = () => {
        const searchId =
            contractData?.contractDetail?.contractId?.id ||
            contractData?.contractName?.contractName ||
            contractId ||
            '';
        if (searchId) {
            sessionStorage.setItem('workflowManagerSearchKey', searchId);
        }
        onClose();
        window.location.pathname = '/app/workflowManager';
    };

    // Handle click on red alert icons to navigate to specific tab
    const handleIconClick = (sectionTitle, status) => {
        if (status === 'not_completed' || status === 'partial') {
            // Workflow sections always navigate to the workflow dashboard (non-amendment flow only)
            if (workflowSectionTitles.has(sectionTitle)) {
                navigateToWorkflowDashboard();
                return;
            }

            const tabName = sectionToTabMapping[sectionTitle];
            console.log('Attempting to navigate to tab:', tabName, 'for section:', sectionTitle);

            if (tabName && onNavigateToTab && typeof onNavigateToTab === 'function') {
                // Close the popup and navigate directly
                onClose();
                onNavigateToTab(tabName);
            } else {
                console.warn('onNavigateToTab callback not available for navigation to:', tabName);
            }
        }
    };

    const handleEditorChange = (event, editor) => {
        const data = editor.getData();
        const plainText = editor.editing.view.document.getRoot().getChild(0)?.getChild(0)?.data || "";
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = data;
        const text = tempDiv.textContent || tempDiv.innerText || "";
        if (text.length <= MAX_CHAR_LIMIT) {
            setUserNotes(data);
            setCharCount(text.length);
        } else {
            editor.setData(userNotes);
        }
    };

    const getActiveContract = async () => {
        const contractStatus = "ACTIVE";
        const serviceProviderIds = contractData?.contractServiceProviders?.map(serviceprovider => serviceprovider?.id) || [];
        const contractedServiceProviderIds =
            contractData?.contractedServices?.flatMap(contractService => contractService?.serviceProviders?.map(serviceprovider => serviceprovider?.id)) || [];
        const allIdsExist = serviceProviderIds.every(id =>
            contractedServiceProviderIds.includes(id)
        );

        const hasAtLeastOneLinked = serviceProviderIds.some(id =>
            contractedServiceProviderIds.includes(id)
        );

        console.log("serviceProviderIds.length", serviceProviderIds.length, serviceProviderIds, contractedServiceProviderIds, hasAtLeastOneLinked, serviceProviderIds.some(id =>
            contractedServiceProviderIds.includes(id)
        ))

        if (serviceProviderIds.length > 0 && !hasAtLeastOneLinked
        ) {
            console.log("serviceProviderIds.length", serviceProviderIds.length, serviceProviderIds, contractedServiceProviderIds, serviceProviderIds.every(id => contractedServiceProviderIds.includes(id)))
            ErrorToaster(
                "Contract activation failed: One or more service providers are not linked to any contracted services."
            );
            return;
        }

        const servicesMissingFromTimesheets = getServicesMissingFromTimesheets(
            contractData?.contractedServices,
            contractData?.timesheetSubmissionTerms
        );
        if (servicesMissingFromTimesheets.length > 0) {
            ErrorToaster(
                `Contracted Services Specification changes are not updated in Timesheet Submission Terms: ${servicesMissingFromTimesheets.join(', ')}`
            );
            return;
        }

        // Assign primary site for the contract's users before activating the contract
        // (only when sites aren't role-scoped, i.e. onlySiteRoleConfigViaGroup is false)
        const onlySiteRoleConfigViaGroup = sessionStorage.getItem('onlySiteRoleConfigViaGroup') !== 'false';
        if (!onlySiteRoleConfigViaGroup) {
            try {
                const primarySiteObj = contractData?.contractDetail?.site?.sites?.find(
                    (site) => site?.primarySite === true
                );
                const { data: sitesList } = await GET("entity-service/sites");
                const matchedSite = (sitesList || []).find(
                    (site) => site?.id === primarySiteObj?.id
                );
                const siteAbbreviation =
                    matchedSite?.siteAbbreviation?.abbreviation ||
                    matchedSite?.siteAbbreviation?.siteAbbr ||
                    "";
                const { data: contractWorkflowData } = await GET(
                    `timesheet-management-service/contractWorkflow/${contractId}`
                );
                const userIdSet = new Set();
                (contractWorkflowData?.timesheetWorkflows || []).forEach((wf) => {
                    (wf?.activityLoggerDetail?.authorizedUserIds || []).forEach((id) => userIdSet.add(id));
                    (wf?.timesheetSubmissionUser?.authorizedUserIds || []).forEach((id) => userIdSet.add(id));
                    (wf?.timesheetWorkflowConfiguration?.authorizedUserIds || []).forEach((id) => userIdSet.add(id));
                });
                (contractWorkflowData?.invoiceWorkflows || []).forEach((iw) => {
                    (iw?.authorizedUserIds || []).forEach((id) => userIdSet.add(id));
                });
                const userIds = Array.from(userIdSet);
                if (siteAbbreviation && userIds.length > 0) {
                    await PUT(
                        "user-management-service/user/contract/assignPrimarySite",
                        JSON.stringify({ siteAbbreviation, userIds })
                    );
                }
            } catch (error) {
                console.error("Error assigning primary site before activation:", error);
            }
        }

        const activationData = {
            contractActivation: {
                activationNotes: {
                    notes: userNotes,
                },
                activatedBy: {
                    id: currentUserData?.id,
                    name: {
                        firstName: currentUserData?.firstName,
                        lastName: currentUserData?.lastName,
                    },
                    email: {
                        officialEmail: currentUserData?.email,
                    },
                },
            },
        };

        try {
            const response = await PUT(
                `contract-managment-service/contracts/${contractId}/contractStatus/${contractStatus}`,
                activationData
            );

            console.log("response", response);
            console.log("123456789", activationData)

            if (response?.response?.status === 409) {
                ErrorToaster(response?.response?.data);
                return;
            }

            if (contractStatus === "ACTIVE") {
                SuccessToaster("Contract Activated Successfully");
                onClose();
            } else {
                ErrorToaster("Contract Activation Pending");
            }
        } catch (error) {
            ErrorToaster("Contract Activation Failed");
        }
        console.log("activationData", activationData, contractStatus, allIdsExist)
    };

    const getDraftContract = async () => {
        const contractStatus = "DRAFT";

        const activationData = {
            contractActivation: {
                activationNotes: {
                    notes: userNotes,
                },
                activatedBy: {
                    id: currentUserData?.id,
                    name: {
                        firstName: currentUserData?.firstName,
                        lastName: currentUserData?.lastName,
                    },
                    email: {
                        officialEmail: currentUserData?.email,
                    },
                },
            },
        };

        try {
            const response = await PUT(
                `contract-managment-service/contracts/${contractId}/contractStatus/${contractStatus}`,
                activationData
            );

            console.log("response", response);
            console.log("123456789", activationData)

            if (response?.response?.status === 409) {
                ErrorToaster(response?.response?.data);
                return;
            }

            if (contractStatus === "DRAFT") {
                SuccessToaster("Contract Rectivated Successfully");
                onClose();
            } else {
                ErrorToaster("Contract Rectivation Pending");
            }
        } catch (error) {
            ErrorToaster("Contract Rectivation Failed");
        }
        console.log("activationData", activationData, contractStatus)
    };

    const getReactiveAmendmentContract = async () => {
        // const contractStatus = "REACTIVATE";

        const activationData = {
            contractActivation: {
                activationNotes: {
                    notes: userNotes,
                },
                activatedBy: {
                    id: currentUserData?.id,
                    name: {
                        firstName: currentUserData?.firstName,
                        lastName: currentUserData?.lastName,
                    },
                    email: {
                        officialEmail: currentUserData?.email,
                    },
                },
            },
        };

        try {
            const response = await PUT(
                `contract-managment-service/amendment/${contractId}/reactivate`
            );

            console.log("response", response);
            console.log("Reactivate response (impactPreview format):", response?.data);
            console.log("123456789", activationData)

            if (response?.response?.status >= 400) {
                console.log("errorMessage", response?.response?.status)
                const errorMessage =
                    response?.response?.data ||
                    "Contract failed to Reactivate";

                ErrorToaster(errorMessage);
                // onClose(); // close popup
                return;
            }

            // if (contractStatus === "DRAFT") {
            SuccessToaster("Contract Reactivated Successfully");
            onClose();
            window.location.pathname = '/app/contract-manager';
            // } else {
            //     ErrorToaster("Contract Rectivation Pending");
            // }
        } catch (error) {
            console.log("errorMessage", error)
            const errorMessage =
                error?.response?.data ||
                "Contract failed to Reactivate";

            ErrorToaster(errorMessage);
            // onClose();
        }
        console.log("activationData1233", activationData)
    };



    const fetchWorkflowData = async (apiContractId) => {
        try {
            console.log('Fetching workflow data for contractId:', apiContractId);
            const response = await GET(`timesheet-management-service/contractWorkflow/${apiContractId}`);
            console.log('Workflow API response:', response.data);
            setWorkflowData(response.data);
        } catch (error) {
            console.error('Error fetching workflow data:', error);
            setWorkflowData(null);
        }
    };

    const fetchAmendmentData = async (apiContractId) => {
        try {
            console.log('Fetching amendment data for contractId:', apiContractId);
            const response = await GET(`contract-managment-service/amendment/contract/${apiContractId}`);
            console.log('Amendment API response:', response.data);
            setAmendmentData(response.data);
        } catch (error) {
            console.error('Error fetching amendment data:', error);
            setAmendmentData(null);
        }
    };


    useEffect(() => {
        console.log('ContractSummary useEffect - isOpen:', isOpen, 'contractId:', contractId, 'isAmendment:', isAmendment);
        if (isOpen) {
            setLoading(true);
            console.log('Fetching contract data for contractId:', contractId);
            const apiContractId = contractId || 'aa1d7e9a-f4df-4c1a-836b-bc6d8313fa51';
            console.log('Making API call for contractId:', apiContractId);

            // Fetch data based on whether it's an amendment or regular contract
            if (isAmendment) {
                // For amendments, fetch both contract and amendment data
                Promise.all([
                    GET(`contract-managment-service/contracts/${apiContractId}`),
                    GET(`contract-managment-service/amendment/${apiContractId}`)
                ])
                    .then(([contractResponse, amendmentResponse]) => {
                        console.log('Contract API response status:', contractResponse.status);
                        console.log('Contract API response data:', contractResponse.data);
                        console.log('Amendment API response:', amendmentResponse.data);

                        const contractData = contractResponse.data.contract || contractResponse.data;
                        setContractData(contractData);
                        setAmendmentData(amendmentResponse.data);
                        setLoading(false);
                    })
                    .catch(error => {
                        console.error('Error fetching amendment data:', error);
                        setContractData(null);
                        setAmendmentData(null);
                        setLoading(false);
                    });
            } else {
                // For regular contracts, fetch contract and workflow data
                Promise.all([
                    GET(`contract-managment-service/contracts/${apiContractId}`),
                    GET(`timesheet-management-service/contractWorkflow/${apiContractId}`)
                ])
                    .then(([contractResponse, workflowResponse]) => {
                        console.log('Contract API response status:', contractResponse.status);
                        console.log('Contract API response data:', contractResponse.data);
                        console.log('Workflow API response:', workflowResponse.data);

                        const contractData = contractResponse.data.contract || contractResponse.data;
                        setContractData(contractData);
                        setWorkflowData(workflowResponse.data);

                        if (contractData.timesheetWorkflows || contractData.invoiceWorkflows) {
                            setWorkflowData({
                                timesheetWorkflows: contractData.timesheetWorkflows || [],
                                invoiceWorkflows: contractData.invoiceWorkflows || []
                            });
                        }

                        setLoading(false);
                    })
                    .catch(error => {
                        console.error('Error fetching contract data:', error);
                        setContractData(null);
                        setWorkflowData(null);
                        setLoading(false);
                    });
            }
        } else {
            console.log('Skipping API call - isOpen:', isOpen);
        }
    }, [isOpen, contractId, isAmendment]);

    let contractInfo = {
        canActivate: true
    };
    let contractSections = [];
    let workflowSections = [];

    const getAmendmentSectionStatus = (sectionKey, amendmentData) => {
        console.log('Getting status for section:', sectionKey);
        console.log('Full amendmentData:', amendmentData);

        if (!amendmentData) {
            console.log('No amendment data available');
            return "inactive";
        }

        // Helper: Get status safely (null amendment → completed)
        const getStatus = (obj) => {
            return obj?.amended?.completionStatus || "COMPLETED";
        };

        switch (sectionKey) {

            // ---------------- CONTRACT DETAILS ----------------
            case 'contractDetails':
                if (amendmentData.contractDetails) {
                    const status = getStatus(amendmentData.contractDetails);
                    console.log('contractDetails status:', status);
                    return status;
                }
                return "inactive";
            // case 'contractDetails': {
            //     const cd = amendmentData.contractDetails;
            //     if (!cd) return "inactive";

            //     // If amended exists, take from amended, else from current
            //     const status =
            //         cd?.amended?.contractDetail?.completionStatus ||
            //         // cd?.current?.contractDetail?.completionStatus ||
            //         "COMPLETED";

            //     console.log('contractDetails resolved status:', status);
            //     return status;
            // }

            // ---------------- CONTRACTED SERVICES ----------------
            case 'contractedServices':
                if (Array.isArray(amendmentData.contractedServices)) {
                    let hasPartial = false;
                    let hasNotCompleted = false;

                    for (const service of amendmentData.contractedServices) {
                        const status = getStatus(service);
                        console.log("Service status:", status);

                        if (status === "NOT_COMPLETED") hasNotCompleted = true;
                        else if (status === "PARTIAL") hasPartial = true;
                    }

                    if (hasNotCompleted) return "NOT_COMPLETED";
                    if (hasPartial) return "PARTIAL";
                    return "COMPLETED";
                }
                return "inactive";

            // ---------------- CONTRACTOR BUSINESS ENTITY ----------------
            case 'contractorBusinessDetails':
                if (amendmentData.contractorBusinessDetails) {
                    const status = getStatus(amendmentData.contractorBusinessDetails);
                    console.log('contractorBusinessEntity status:', status);
                    return status;
                }
                return "inactive";

            // ---------------- PAYMENT & COMPENSATION ----------------
            case 'paymentAndCompensation':
                if (amendmentData.paymentAndCompensation) {
                    const status = getStatus(amendmentData.paymentAndCompensation);
                    console.log('paymentAndCompensation status:', status);
                    return status;
                }
                return "inactive";

            // ---------------- TIMESHEET SUBMISSION TERMS ----------------
            case 'timesheetSubmissionTerms':
                if (Array.isArray(amendmentData.timesheetSubmissionTerms)) {
                    let hasPartial = false;
                    let hasNotCompleted = false;

                    for (const term of amendmentData.timesheetSubmissionTerms) {
                        const status = getStatus(term);
                        console.log("Timesheet term status:", status);

                        if (status === "NOT_COMPLETED") hasNotCompleted = true;
                        else if (status === "PARTIAL") hasPartial = true;
                    }

                    if (hasNotCompleted) return "NOT_COMPLETED";
                    if (hasPartial) return "PARTIAL";
                    return "COMPLETED";
                }
                return "inactive";

            default:
                console.log('Unknown section key:', sectionKey);
                return "inactive";
        }
    };

    const isAnySectionNotCompleted = (amendmentData) => {
        const sections = [
            "contractDetails",
            "contractedServices",
            "contractorBusinessDetails",
            "paymentAndCompensation",
            "timesheetSubmissionTerms"
        ];

        for (const key of sections) {
            const status = getAmendmentSectionStatus(key, amendmentData);
            if (status === "NOT_COMPLETED") return true;
        }
        return false;
    };


    const disableReactivate = isAmendment && isAnySectionNotCompleted(amendmentData);

    console.log("disableReactivate", disableReactivate, isAmendment)


    const getWorkflowSectionStatus = (sectionTitle, workflowData) => {
        if (!workflowData) {
            return "inactive";
        }

        switch (sectionTitle) {
            case "Activity Logging":
                if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
                    const hasIncompleteActivityLogging = workflowData.timesheetWorkflows.some(workflow => {
                        const activityLogDetail = workflow.activityLoggerDetail;
                        return !activityLogDetail || activityLogDetail.completionStatus !== "COMPLETED";
                    });

                    const hasPartialActivityLogging = workflowData.timesheetWorkflows.some(workflow => {
                        const activityLogDetail = workflow.activityLoggerDetail;
                        return activityLogDetail && activityLogDetail.completionStatus === "PARTIAL";
                    });

                    if (hasIncompleteActivityLogging) {
                        return hasPartialActivityLogging ? "partial" : "not_completed";
                    }
                    return "completed";
                }
                return "inactive";

            case "Timesheet Creation & Submission":
                if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
                    const hasSubmissionUsers = workflowData.timesheetWorkflows.some(workflow =>
                        workflow.timesheetSubmissionUser !== null
                    );

                    if (!hasSubmissionUsers) {
                        return "not_completed";
                    }

                    const hasIncompleteSubmission = workflowData.timesheetWorkflows.some(workflow => {
                        const submissionUser = workflow.timesheetSubmissionUser;
                        return submissionUser && submissionUser.completionStatus !== "COMPLETED";
                    });

                    const hasPartialSubmission = workflowData.timesheetWorkflows.some(workflow => {
                        const submissionUser = workflow.timesheetSubmissionUser;
                        return submissionUser && submissionUser.completionStatus === "PARTIAL";
                    });

                    if (hasIncompleteSubmission) {
                        return hasPartialSubmission ? "partial" : "not_completed";
                    }
                    return "completed";
                }
                return "inactive";

            case "Timesheet Review & Approval":
                if (workflowData.timesheetWorkflows && workflowData.timesheetWorkflows.length > 0) {
                    const hasWorkflowConfig = workflowData.timesheetWorkflows.some(workflow =>
                        workflow.timesheetWorkflowConfiguration !== null
                    );

                    if (!hasWorkflowConfig) {
                        return "not_completed";
                    }

                    const hasIncompleteWorkflow = workflowData.timesheetWorkflows.some(workflow => {
                        const config = workflow.timesheetWorkflowConfiguration;
                        if (!config) return true;

                        if (config.completionStatus === "NOT_COMPLETED") return true;
                        if (!config.hasTimesheetApproval) return false;
                        if (!config.authorizedUserIds || config.authorizedUserIds.length === 0) return true;

                        return false;
                    });

                    const hasPartialWorkflow = workflowData.timesheetWorkflows.some(workflow => {
                        const config = workflow.timesheetWorkflowConfiguration;
                        return config && config.completionStatus === "PARTIAL";
                    });

                    if (hasIncompleteWorkflow) {
                        return hasPartialWorkflow ? "partial" : "not_completed";
                    }
                    return "completed";
                }
                return "inactive";

            case "Invoice Review & Approval":
                if (workflowData.invoiceWorkflows && workflowData.invoiceWorkflows.length > 0) {
                    const hasIncompleteInvoice = workflowData.invoiceWorkflows.some(invoiceWorkflow => {
                        if (invoiceWorkflow.completionStatus === "NOT_COMPLETED") return true;
                        // if (!invoiceWorkflow.controlLevels) return true;
                        // if (!invoiceWorkflow.authorizedUserIds || invoiceWorkflow.authorizedUserIds.length === 0) return true;

                        return false;
                    });

                    const hasPartialInvoice = workflowData.invoiceWorkflows.some(invoiceWorkflow => {
                        return invoiceWorkflow.completionStatus === "PARTIAL";
                    });

                    if (hasIncompleteInvoice) {
                        return hasPartialInvoice ? "partial" : "not_completed";
                    }
                    return "completed";
                }
                return "inactive";

            default:
                return "inactive";
        }
    };

    if (contractData && contractData.id) {
        console.log('Processing new API structure:', contractData, 'isAmendment:', isAmendment);

        let hasDocuments = false;
        let documentCount = 0;

        // if (contractData.contractDetail?.contractFiles && contractData.contractDetail.contractFiles.length > 0) {
        //     const validDocuments = contractData.contractDetail.contractFiles.filter(file =>
        //         file || file.fileURL || file.fileURL.trim() !== ''
        //     );

        if (contractData.contractDetail.contractFiles.length > 0) {
            hasDocuments = true;
            documentCount = contractData.contractDetail.contractFiles.length;
        }
        // }

        console.log('Document check (new API):', { hasDocuments, documentCount, contractFiles: contractData.contractDetail?.contractFiles });
        console.log('DEBUG - Full contractData.contractDetail:', contractData.contractDetail);
        console.log('DEBUG - contractFiles raw:', contractData.contractDetail?.contractFiles);
        if (contractData.contractDetail?.contractFiles) {
            contractData.contractDetail.contractFiles.forEach((file, index) => {
                console.log(`DEBUG - File ${index}:`, {
                    file: file,
                    hasFileURL: !!file?.fileURL,
                    fileURL: file?.fileURL,
                    fileURLTrimmed: file?.fileURL?.trim()
                });
            });
        }

        contractInfo = {
            contractTitle: contractData.contractName?.contractName || '',
            contractId: contractData.contractDetail?.contractId?.id || contractData.id || '',
            contractSubtitle: contractData.contractType === "INDIVIDUAL" ? "Solo Provider Contract" : "Multiple Provider Contract",
            lastUpdated: contractData.lastModifiedDate || '',
            isDraft: contractData.contractStatus === 'DRAFT',
            alerts: [],
        };

        // Create contract sections based on amendment or regular flow
        if (isAmendment) {
            // Amendment flow - show only relevant sections

            // Special logic for Attached Contract Documents in amendment
            const getAmendmentDocumentStatus = () => {
                console.log('=== DOCUMENT STATUS DEBUG ===');
                console.log('Full amendmentData:', amendmentData);
                console.log('Full contractData for current documents:', contractData);

                // Check if amendmentData exists
                if (!amendmentData) {
                    console.log('No amendmentData available - checking only current contract documents');
                    // If no amendment data, check if current contract has documents
                    if (contractData?.contractDetail?.contractFiles && contractData.contractDetail.contractFiles.length > 0) {
                        return "partial"; // Current has documents, amendment status unknown
                    }
                    return "not_completed"; // No documents anywhere
                }

                // Get current document count - check both contractData and amendmentData
                let currentFiles = [];
                let currentCount = 0;

                // First check contractData (the actual current contract being viewed)
                if (contractData?.contractDetail?.contractFiles) {
                    currentFiles = contractData.contractDetail.contractFiles;
                    // Apply same validation filter as main flow
                    const validCurrentDocs = currentFiles.filter(file =>
                        file || file.fileURL || file.fileURL.trim() !== ''
                    );
                    currentCount = validCurrentDocs.length;
                    console.log('Found current documents in contractData:', currentFiles);
                    console.log('Valid current documents after filtering:', validCurrentDocs);
                }
                // Fallback to amendmentData structure
                else if (amendmentData?.contractDetails?.current?.contractDetail?.contractFiles) {
                    currentFiles = amendmentData.contractDetails.current.contractDetail.contractFiles;
                    // Apply same validation filter as main flow
                    const validCurrentDocs = currentFiles.filter(file =>
                        file || file.fileURL || file.fileURL.trim() !== ''
                    );
                    currentCount = validCurrentDocs.length;
                    console.log('Found current documents in amendmentData:', currentFiles);
                    console.log('Valid current documents after filtering:', validCurrentDocs);
                }

                console.log('Final current document count:', currentCount);

                // Get amendment document count  
                console.log('amendmentData.contractDetails:', amendmentData.contractDetails);

                // Check if contractDetails exists
                if (!amendmentData.contractDetails) {
                    console.log('No contractDetails in amendmentData');
                    // If current has documents but no amendment data, return partial
                    if (currentCount > 0) {
                        return "partial";
                    }
                    return "not_completed";
                }

                console.log('amendmentData.contractDetails.amended:', amendmentData.contractDetails.amended);

                // Get amendment contract files
                let amendmentFiles = [];

                // amendmentData.contractDetails.amended.contractDetail.contractFiles
                if (amendmentData.contractDetails.amended?.contractDetail?.contractFiles) {
                    amendmentFiles = amendmentData.contractDetails.amended.contractDetail.contractFiles;
                    console.log('Found amendment files:', amendmentFiles);
                }

                // Apply same validation filter for amendment files
                const validAmendmentFiles = amendmentFiles.filter(file =>
                    file || file.fileURL || file.fileURL.trim() !== ''
                );
                const amendmentCount = validAmendmentFiles.length;
                console.log('Final amendment document files:', amendmentFiles);
                console.log('Valid amendment files after filtering:', validAmendmentFiles);
                console.log('Amendment document count:', amendmentCount);

                console.log('Document count comparison: current =', currentCount, ', amendment =', amendmentCount);

                // Updated logic for amendment flow:
                // 1. If both current and amendment have documents → green (completed)
                if (currentCount > 0 && amendmentCount > 0) {
                    console.log('Both current and amendment have documents - showing completed');
                    return "completed";
                }
                // 2. If current has at least one document and amendment has 0 → yellow (partial)
                else if (currentCount > 0 && amendmentCount === 0) {
                    console.log('Current has documents but amendment has none - showing partial');
                    return "partial";
                }
                // 3. If both have 0 documents → red (not_completed)
                else {
                    console.log('Both current and amendment have no documents - showing not_completed');
                    return "not_completed";
                }
            };

            const amendmentDocStatus = getAmendmentDocumentStatus();


            let amendmentDocCount = 0;
            let amendmentFilesForCount = [];
            let validAmendmentDocsForCount = []; // Declare in broader scope
            if (amendmentData?.contractDetails?.amended?.contractDetail?.contractFiles) {
                amendmentFilesForCount = amendmentData.contractDetails.amended.contractDetail.contractFiles;
                // Apply validation filter for amendment documents
                validAmendmentDocsForCount = amendmentFilesForCount.filter(file =>
                    file || file.fileURL || file.fileURL.trim() !== ''
                );
                amendmentDocCount = validAmendmentDocsForCount.length;
            }


            let currentDocCount = 0;
            let currentDocsForCount = [];
            if (contractData?.contractDetail?.contractFiles) {
                const validCurrentDocsForCount = contractData.contractDetail.contractFiles.filter(file =>
                    file || file.fileURL || file.fileURL.trim() !== ''
                );
                currentDocCount = validCurrentDocsForCount.length;
                currentDocsForCount = validCurrentDocsForCount;
            } else if (amendmentData?.contractDetails?.current?.contractDetail?.contractFiles) {
                const validCurrentDocsForCount = amendmentData.contractDetails.current.contractDetail.contractFiles.filter(file =>
                    file || file.fileURL || file.fileURL.trim() !== ''
                );
                currentDocCount = validCurrentDocsForCount.length;
                currentDocsForCount = validCurrentDocsForCount;
            }

            // Calculate unique document count by combining and deduplicating by ID
            const allDocuments = [...currentDocsForCount, ...validAmendmentDocsForCount];
            const uniqueDocuments = allDocuments.reduce((unique, doc) => {
                // Check if document with same ID already exists
                const existingDoc = unique.find(existingDoc => existingDoc.id === doc.id);
                if (!existingDoc) {
                    unique.push(doc);
                }
                return unique;
            }, []);

            const uniqueDocumentCount = uniqueDocuments.length;

            // Calculate subtitle based on document status using unique count
            let documentSubtitle = "No Documents";
            if (amendmentDocStatus === "completed") {
                // Both have documents - show unique count
                console.log("uniqueDocumentCount", uniqueDocumentCount)
                documentSubtitle = `${uniqueDocumentCount} Documents`;
            } else if (amendmentDocStatus === "partial") {
                // Only current has documents
                documentSubtitle = `${currentDocCount} Documents`;
                console.log("uniqueDocumentCount", currentDocCount)
            } else {
                // Both have 0 documents
                documentSubtitle = "No Documents";
            }

            console.log('Document count for subtitle - current:', currentDocCount, 'amendment:', amendmentDocCount, 'subtitle:', documentSubtitle);

            contractSections = [
                {
                    title: "Attached Contract Document(s)",
                    subtitle: documentSubtitle,
                    status: amendmentDocStatus
                },
                {
                    title: "Contract Identification & Term Limit",
                    status: (() => {
                        const status = getAmendmentSectionStatus('contractDetails', amendmentData);
                        if (status === "COMPLETED") return "completed";
                        if (status === "PARTIAL") return "partial";
                        return "not_completed";
                    })()
                },
                {
                    title: "Contractor Business Entity",
                    status: (() => {
                        const status = getAmendmentSectionStatus('contractorBusinessDetails', amendmentData);
                        if (status === "COMPLETED") return "completed";
                        if (status === "PARTIAL") return "partial";
                        return "not_completed";
                    })()
                },
                {
                    title: "Contracted Services Specification",
                    status: (() => {
                        const status = getAmendmentSectionStatus('contractedServices', amendmentData);
                        if (status === "COMPLETED") return "completed";
                        if (status === "PARTIAL") return "partial";
                        return "not_completed";
                    })()
                },
                {
                    title: "Timesheet Submission Terms",
                    status: (() => {
                        const status = getAmendmentSectionStatus('timesheetSubmissionTerms', amendmentData);
                        if (status === "COMPLETED") return "completed";
                        if (status === "PARTIAL") return "partial";
                        return "not_completed";
                    })()
                },
                {
                    title: "Payment & Compensation",
                    status: (() => {
                        const status = getAmendmentSectionStatus('paymentAndCompensation', amendmentData);
                        if (status === "COMPLETED") return "completed";
                        if (status === "PARTIAL") return "partial";
                        return "not_completed";
                    })()
                }
            ];
        } else {
            // Regular flow - show all sections

            // Per-provider timesheet check: if enabled, find providers not linked to any timesheet
            let timesheetPerProviderTooltip = null;
            if (contractData.timesheetSubmissionTermPerProvider) {
                const allProviders = contractData.contractServiceProviders || [];
                const timesheetTermsArrayForCheck = Array.isArray(contractData.timesheetSubmissionTerms)
                    ? contractData.timesheetSubmissionTerms
                    : (contractData.timesheetSubmissionTerms ? [contractData.timesheetSubmissionTerms] : []);
                const linkedProviderIds = new Set(
                    timesheetTermsArrayForCheck.map(t => t.serviceProviderId).filter(Boolean)
                );
                const unlinkedProviders = allProviders.filter(p => !linkedProviderIds.has(p.id));
                if (unlinkedProviders.length > 0) {
                    const names = unlinkedProviders.map(p => p.serviceProviderName).join(', ');
                    timesheetPerProviderTooltip = `The following service provider(s) are not linked to a timesheet: ${names}`;
                }
            }

            // Detect contracted services (by activity type) added/changed that aren't
            // yet reflected in any timesheet submission term, mirroring the backend's
            // own activation check so the user sees the red alert before they hit Activate.
            const servicesMissingFromTimesheets = getServicesMissingFromTimesheets(
                contractData.contractedServices,
                contractData.timesheetSubmissionTerms
            );
            const servicesSyncTooltip = servicesMissingFromTimesheets.length > 0
                ? `Contracted Services Specification changes are not updated in Timesheet Submission Terms: ${servicesMissingFromTimesheets.join(', ')}`
                : null;
            const timesheetTermsTooltip = [servicesSyncTooltip, timesheetPerProviderTooltip].filter(Boolean).join(' | ');

            contractSections = [
                {
                    title: "Attached Contract Document(s)",
                    subtitle: hasDocuments ? `${documentCount} Documents` : "No Documents",
                    status: hasDocuments ? "completed" : "not_completed"
                },
                {
                    title: "Contract Identification & Term Limit",
                    status: (() => {
                        console.log('contractData.contractDetail', contractData.completionStatus)
                        if (contractData.completionStatus === "COMPLETED") {
                            console.log('contractData.contractDetail', contractData.contractDetail)
                            return "completed";
                        }
                        if (contractData.completionStatus === "PARTIAL") {
                            console.log('contractData.contractDetail', contractData.contractDetail)
                            return "partial";
                        }
                        if (contractData.completionStatus === "NOT_COMPLETED") {
                            console.log('contractData.contractDetail', contractData.contractDetail)
                            return "not_completed";
                        }

                        // if (contractData.contractDetail) {
                        //     return "completed";
                        // }
                        return "inactive";
                    })()
                },
                {
                    title: "Contracted Services Provider(s)",
                    status: (() => {
                        if (contractData.contractServiceProviders?.length > 0) {
                            const hasIncompleteProviders = contractData.contractServiceProviders.some(provider => {
                                const hasBasicInfo = provider.name?.firstName &&
                                    provider.name?.lastName &&
                                    provider.contractedServiceProviderType?.id;

                                return !hasBasicInfo;
                            });

                            if (hasIncompleteProviders) {
                                return "not_completed";
                            } else {
                                return "completed";
                            }
                        } else {
                            return "completed";
                        }
                    })()
                },
                {
                    title: "Contractor Business Entity",
                    status: (() => {
                        if (!contractData.contractorBusinessEntity || contractData.contractorBusinessEntity.id === null) {
                            return "inactive";
                        }

                        if (contractData.contractorBusinessEntity.completionStatus === "NOT_COMPLETED") {
                            return "not_completed";
                        }
                        if (contractData.contractorBusinessEntity.completionStatus === "PARTIAL") {
                            return "partial";
                        }
                        if (contractData.contractorBusinessEntity.completionStatus === "COMPLETED") {
                            return "completed";
                        }

                        return "inactive";
                    })()
                },
                {
                    title: "Contracted Services Specification",
                    status: (() => {
                        if (!contractData.contractedServices || contractData.contractedServices.length === 0) {
                            return "inactive";
                        }


                        const hasNotCompletedServices = contractData.contractedServices.some(service =>
                            service.completionStatus === "NOT_COMPLETED"
                        );
                        const hasPartialServices = contractData.contractedServices.some(service =>
                            service.completionStatus === "PARTIAL"
                        );
                        const hasCompletedServices = contractData.contractedServices.some(service =>
                            service.completionStatus === "COMPLETED"
                        );

                        if (hasNotCompletedServices) {
                            return "not_completed";
                        }
                        if (hasPartialServices) {
                            return "partial";
                        }
                        if (hasCompletedServices) {
                            return "completed";
                        }

                        return "inactive";
                    })()
                },
                {
                    title: "Timesheet Submission Terms",
                    ...(timesheetTermsTooltip ? { tooltip: timesheetTermsTooltip } : {}),
                    status: (() => {
                        const timesheetTermsArray = Array.isArray(contractData.timesheetSubmissionTerms) ?
                            contractData.timesheetSubmissionTerms :
                            (contractData.timesheetSubmissionTerms ? [contractData.timesheetSubmissionTerms] : []);

                        if (timesheetTermsArray.length === 0) {
                            return "inactive";
                        }

                        const hasNotCompletedTerms = timesheetTermsArray.some(terms =>
                            terms.completionStatus === "NOT_COMPLETED"
                        );
                        const hasPartialTerms = timesheetTermsArray.some(terms =>
                            terms.completionStatus === "PARTIAL"
                        );
                        const hasCompletedTerms = timesheetTermsArray.some(terms =>
                            terms.completionStatus === "COMPLETED"
                        );

                        if (hasNotCompletedTerms) {
                            return "not_completed";
                        }
                        // Services changed but not reflected in the timesheet, or
                        // per-provider unlinked providers: both override completed/partial status
                        if (servicesMissingFromTimesheets.length > 0 || timesheetPerProviderTooltip) {
                            return "not_completed";
                        }
                        if (hasPartialTerms) {
                            return "partial";
                        }
                        if (hasCompletedTerms) {
                            return "completed";
                        }

                        return "inactive";
                    })()
                },
                {
                    title: "Payment & Compensation",
                    status: (() => {
                        if (!contractData.paymentAndCompensation) {
                            return "inactive";
                        }
                        if (contractData.paymentAndCompensation.completionStatus === "NOT_COMPLETED") {
                            return "not_completed";
                        }
                        if (contractData.paymentAndCompensation.completionStatus === "PARTIAL") {
                            return "partial";
                        }
                        if (contractData.paymentAndCompensation.completionStatus === "COMPLETED") {
                            return "completed";
                        }

                        return "inactive";
                    })()
                }
            ];
        }

        //             return "inactive";
        //         })()
        //     }
        // ];

        workflowSections = [
            {
                title: "Activity Logging",
                status: getWorkflowSectionStatus("Activity Logging", workflowData)
            },
            {
                title: "Timesheet Creation & Submission",
                status: getWorkflowSectionStatus("Timesheet Creation & Submission", workflowData)
            },
            {
                title: "Timesheet Review & Approval",
                status: getWorkflowSectionStatus("Timesheet Review & Approval", workflowData)
            },
            // Only include Invoice section if invoiceWorkflowControlLevelBy is "CONTRACT" (using fresh site data)
            ...(siteInvoiceControlLevel === "CONTRACT" ? [{
                title: "Invoice Review & Approval",
                status: getWorkflowSectionStatus("Invoice Review & Approval", workflowData)
            }] : [])
        ];

        console.log('Workflow Data:', workflowData);
        console.log('Amendment Data:', amendmentData);
        console.log('isAmendment:', isAmendment);
        console.log('Contract Sections:', contractSections.map(section => ({ title: section.title, status: section.status })));
        console.log('Workflow Sections Status:', workflowSections.map(section => ({ title: section.title, status: section.status })));

        const alerts = [];

        const sectionsWithWarnings = contractSections.filter(section => section.status === 'not_completed');
        const sectionsWithpartials = contractSections.filter(section => section.status === 'partial');

        if (sectionsWithWarnings.length > 0) {
            alerts.push('Contract Data Required');
        }
        else if (sectionsWithpartials.length > 0) {
            alerts.push('Contract Data Partially Completed');
        }
        else {
            alerts.push('Contract Data Completed');
        }

        // Only add workflow alerts for non-amendment flow
        if (!isAmendment) {
            const incompleteWorkflows = workflowSections.filter(section => section.status === 'inactive' || section.status === 'not_completed');
            const partialWorkflows = workflowSections.filter(section => section.status === 'partial');

            if (incompleteWorkflows.length > 0) {
                alerts.push('Workflow Configuration Incomplete');
            }
            else if (partialWorkflows.length > 0) {
                alerts.push('Workflow Configuration Partially Completed');
            }
            else {
                alerts.push('Workflow Configuration Completed');
            }
        }

        contractInfo.alerts = alerts;

        // For amendment flow, allow both 'completed' and 'partial' status
        const allSectionsCompleted = isAmendment ?
            contractSections.every(section => section.status === 'completed' || section.status === 'partial') :
            contractSections.every(section => section.status === 'completed');
        const allWorkflowsCompleted = isAmendment ? true : workflowSections.every(section => section.status === 'completed');
        contractInfo.canActivate = allSectionsCompleted && allWorkflowsCompleted;
    }
    const amendmentContractMetaSections = new Set([
        "Contract Identification & Term Limit",
        "Contractor Business Entity",
    ]);

    const getRedAlertTooltip = (sectionTitle) => {
        if (workflowSectionTitles.has(sectionTitle)) {
            return `Click here to go to Workflow Dashboard`;
        }
        if (isAmendment && amendmentContractMetaSections.has(sectionTitle)) {
            return `Click here to go to General Contract Updates to Make`;
        }
        const tabName = sectionToTabMapping[sectionTitle] || sectionTitle;
        return `Click here to go to ${tabName}`;
    };

    const getStatusIcon = (status, sectionTitle, customTooltip) => {
        switch (status) {
            case 'completed':
                return <CheckIcon className={styles.completedIcon} />;
            case 'not_completed':
                return (
                    <Tooltip title={customTooltip || getRedAlertTooltip(sectionTitle)} arrow placement="left">
                        <img
                            src={Warning}
                            alt="Alert"
                            className={styles.alertIcon}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleIconClick(sectionTitle, status)}
                        />
                    </Tooltip>
                );
            case 'partial':
                return <img
                    src={yellowWarning}
                    alt="Yellow Warning"
                    className={styles.alertIcon}
                    onClick={() => handleIconClick(sectionTitle, status)}
                />
            case 'inactive':
                return <div className={styles.inactiveIcon}>●</div>;
            default:
                return null;
        }
    };

    const getAlertIcons = () => {
        const alertIcons = [];

        const sectionsWithWarnings = contractSections.filter(section => section.status === 'not_completed');
        const sectionsWithPartials = contractSections.filter(section => section.status === 'partial');
        const allContractSectionsCompleted = contractSections.every(section => section.status === 'completed');

        if (sectionsWithWarnings.length > 0) {
            alertIcons.push(fileIcon);
        } else if (sectionsWithPartials.length > 0) {
            alertIcons.push(yellowWarning);
        } else if (allContractSectionsCompleted) {
            alertIcons.push(CompletedIcon);
        } else {
            alertIcons.push(fileIcon);
        }

        // Only add workflow icons for non-amendment flow
        if (!isAmendment) {
            const incompleteWorkflows = workflowSections.filter(section => section.status === 'inactive' || section.status === 'not_completed');
            const partialWorkflows = workflowSections.filter(section => section.status === 'partial');
            const allWorkflowsCompleted = workflowSections.every(section => section.status === 'completed');

            if (incompleteWorkflows.length > 0) {
                alertIcons.push(fileIcon);
            } else if (partialWorkflows.length > 0) {
                alertIcons.push(yellowWarning);
            } else if (allWorkflowsCompleted) {
                alertIcons.push(CompletedIcon);
            } else {
                alertIcons.push(fileIcon);
            }
        }

        return alertIcons;
    };

    const getAlertIcon = () => {
        const sectionsWithWarnings = contractSections.filter(section => section.status === 'not_completed');
        const sectionsWithPartials = contractSections.filter(section => section.status === 'partial');
        const allContractSectionsCompleted = contractSections.every(section => section.status === 'completed');

        const contractDataComplete = allContractSectionsCompleted && sectionsWithWarnings.length === 0 && sectionsWithPartials.length === 0;

        // For amendment flow, only consider contract data
        if (isAmendment) {
            if (sectionsWithWarnings.length > 0) {
                return fileIcon;
            }
            if (sectionsWithPartials.length > 0) {
                return yellowWarning;
            }
            if (contractDataComplete) {
                return CompletedIcon;
            }
            return fileIcon;
        }

        // For regular flow, consider both contract and workflow data
        const workflowWithWarnings = workflowSections.filter(section => section.status === 'not_completed');
        const workflowWithPartials = workflowSections.filter(section => section.status === 'partial');
        const incompleteWorkflows = workflowSections.filter(section => section.status === 'inactive' || section.status === 'not_completed');
        const allWorkflowsCompleted = workflowSections.every(section => section.status === 'completed');
        const workflowDataComplete = allWorkflowsCompleted && workflowWithWarnings.length === 0 && workflowWithPartials.length === 0 && incompleteWorkflows.length === 0;

        if (sectionsWithWarnings.length > 0 || workflowWithWarnings.length > 0) {
            return fileIcon;
        }

        if ((contractDataComplete && !workflowDataComplete) ||
            (!contractDataComplete && workflowDataComplete) ||
            sectionsWithPartials.length > 0 ||
            workflowWithPartials.length > 0 ||
            incompleteWorkflows.length > 0) {
            return yellowWarning;
        }

        if (contractDataComplete && workflowDataComplete) {
            return CompletedIcon;
        }

        return fileIcon;
    };

    if (!isOpen) return null;

    return (
        <Dialog isOpen={isOpen} className={`${styles.dialogStyle} ${styles.dialogPaddingBottom}`}>
            <div className={`${Classes.DIALOG_BODY} ${styles.extensionDialogBackground}`}>
                <div className={styles.spaceBetween}>
                    <h2 className={styles.extensionStyle}>Contract Summary</h2>
                    <Icon icon="cross" size={20} intent={Intent.DANGER} className={styles.crossStyle} onClick={onClose} />
                </div>
                <div className={styles.extensionBorder}></div>
                {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <p>Loading contract data...</p>
                    </div>
                ) : (
                    <>
                        <div style={{ paddingTop: '10px' }}>
                            <ContractHeader
                                contractTitle={contractInfo.contractTitle}
                                isDraft={contractInfo.isDraft}
                                contractSubtitle={contractInfo.contractSubtitle}
                                contractId={contractInfo.contractId}
                                alerts={contractInfo.alerts}
                                lastUpdated={
                                    contractInfo.lastUpdated
                                        ? format(new Date(contractInfo.lastUpdated), "MMM dd, yyyy")
                                        : "NA"
                                }
                                lastUpdatedBy={workflowData?.lastModifiedBy?.name?.name}
                                alertIcon={getAlertIcon()}
                                alertIcons={getAlertIcons()}
                            />
                        </div>

                        <div className={styles.sectionsContainer}>
                            {contractSections.map((section, index) => (
                                <div key={index} className={styles.sectionRow}>
                                    <div className={styles.sectionContent}>
                                        <span className={styles.sectionTitle}>
                                            {section.title}
                                            {section.subtitle && (
                                                <span className={styles.sectionSubtitle}> {section.subtitle}</span>
                                            )}
                                        </span>
                                    </div>
                                    <div className={styles.sectionStatus}>
                                        {getStatusIcon(section.status, section.title, section.tooltip)}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Only show workflow section for regular (non-amendment) flow */}
                        {!isAmendment && (
                            <div className={styles.workflowSection}>
                                <h2 className={styles.extensionStyle}>Associated Work Flow Configuration</h2>
                                <div className={styles.workflowContainer}>
                                    {workflowSections.map((section, index) => (
                                        <div key={index} className={styles.sectionRow}>
                                            <div className={styles.sectionContent}>
                                                <span className={styles.sectionTitle}>{section.title}</span>
                                            </div>
                                            <div className={styles.sectionStatus}>
                                                {getStatusIcon(section.status, section.title)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(showActivationSection || isAmendment) && (
                            <div className={`${styles.marginTop10}`}>
                                <div className={styles.editorHeading}>{(contractData?.contractStatus === "SOFT_DELETE" || isAmendment) ? "Reactivation Notes & Comments" : "Activation Notes & Comments"}  </div>
                                <CKEditor
                                    editor={ClassicEditor}
                                    data={userNotes}
                                    // onChange={(event, editor) => {
                                    //     const data = editor.getData();
                                    //     setUserNotes(data);

                                    // }}
                                    onChange={handleEditorChange}
                                    config={{
                                        placeholder: "Enter your notes and comments here",
                                        toolbar: {
                                            shouldNotGroupWhenFull: true,
                                            sticky: true,
                                            items: [
                                                'undo', 'redo',
                                                '|',
                                                'heading',
                                                '|',
                                                'fontfamily', 'fontsize', 'fontColor', 'fontBackgroundColor',
                                                '|',
                                                'bold', 'italic', 'strikethrough', 'subscript', 'superscript', 'code',
                                                '|',
                                                'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent'
                                            ],
                                        },
                                        autoGrow: false,
                                        // disableNativeSpellChecker: false,        
                                        // extraPlugins: [WProofreader],
                                        // wproofreader: {
                                        //   // serviceId: 'your-service-id', // Replace with your service ID
                                        //   srcUrl: 'https://svc.webspellchecker.net/spellcheck31/wscbundle/wscbundle.js',
                                        // },
                                    }}
                                    onReady={(editor) => {
                                        const editorElement = editor.editing.view.document.getRoot();
                                        editor.editing.view.change(writer => {
                                            writer.setStyle(
                                                'min-height',
                                                '150px',
                                                editorElement
                                            );
                                        });
                                    }}

                                />
                                <div style={{ textAlign: 'right', marginTop: '4px', fontSize: '12px', color: charCount > MAX_CHAR_LIMIT ? 'red' : 'gray' }}>
                                    {charCount} / {MAX_CHAR_LIMIT}
                                </div>
                                <div className={`${styles.spaceBetween} ${styles.marginTop10}`}>
                                    <div className={`${styles.closeBackgroundStyle} ${styles.closetextStyle} ${styles.crossStyle} `} onClick={onClose}>Close</div>
                                    {/* {contractData?.contractStatus !=="SOFT_DELETE" ? (
                                        <div className={`${contractInfo?.canActivate  ? styles.activateBackgroundStyle : styles.activateDisableBackgroundStyle} ${styles.activeTextStyle}`}
                                     onClick={() => contractInfo?.canActivate && getActiveContract()}
                                    >
                                        ACTIVATE CONTRACT FOR USE
                                    </div> */}
                                    {(contractData?.contractStatus !== "SOFT_DELETE" && !isAmendment) ? (
                                        !contractInfo?.canActivate ? (
                                            <Tooltip title="Contract Summary is incomplete. Please complete all required fields before activating the contract." arrow placement="top"
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: '#D32F2F',
                                                            color: '#fff',
                                                            fontSize: '12px',
                                                            maxWidth: 220,
                                                        },
                                                    },
                                                }}>
                                                <div
                                                    className={`${styles.activateDisableBackgroundStyle} ${styles.activeTextStyle}`}
                                                    style={{ cursor: 'not-allowed' }}
                                                >
                                                    ACTIVATE CONTRACT FOR USE
                                                </div>
                                            </Tooltip>
                                        ) : (
                                            <div
                                                className={`${styles.activateBackgroundStyle} ${styles.activeTextStyle}`}
                                                onClick={getActiveContract}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                ACTIVATE CONTRACT FOR USE
                                            </div>
                                        )
                                    ) : (
                                        <div
                                            // className={`${styles.activateBackgroundStyle} ${styles.activeTextStyle}`}
                                            // onClick={!isAmendment ? () => getDraftContract() : getReactiveContract()}
                                            className={
                                                disableReactivate
                                                    ? `${styles.activateDisableBackgroundStyle} ${styles.activeTextStyle}`
                                                    : `${styles.activateBackgroundStyle} ${styles.activeTextStyle}`
                                            }
                                            style={{
                                                cursor: disableReactivate ? "not-allowed" : "pointer",
                                            }}
                                            onClick={() => {
                                                // if (disableReactivate) return;
                                                if (!isAmendment) {
                                                    getDraftContract();
                                                } else {
                                                    getReactiveAmendmentContract();
                                                }
                                            }}
                                        >
                                            REACTIVATE CONTRACT FOR USE
                                        </div>

                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Dialog >
    );
};

export default ContractSummary;
